<template>
  <v-container fluid>
    <v-row>
      <v-col cols="4" class="pa-0 mt-2">
        <v-card>
          <v-card-title class="pb-2">
            Workflow-Vorlagen
            <v-spacer />
            <v-btn small color="primary" @click="newWorkflow">
              <v-icon left small>mdi-plus</v-icon>
              Neu
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-0">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              placeholder="Suchen…"
              outlined
              dense
              hide-details
              clearable
              single-line
            />
            <v-list dense class="mt-2 wf-list">
              <v-list-item
                v-for="wf in filteredWorkflows"
                :key="wf.id"
                :input-value="isSelected(wf.id)"
                color="primary"
                @click="selectWorkflow(wf.id)"
              >
                <v-list-item-icon class="mr-2 my-2">
                  <v-icon small>mdi-file-tree</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ wf.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ wf.nodes ? wf.nodes.length : 0 }} Knoten
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="workflows.length === 0">
                <v-list-item-content>
                  <v-list-item-subtitle class="grey--text">
                    Keine Vorlagen angelegt.
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-else-if="filteredWorkflows.length === 0">
                <v-list-item-content>
                  <v-list-item-subtitle class="grey--text">
                    Keine Treffer für „{{ search }}".
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="8">
        <WorkflowEditor
          v-if="draft !== null"
          :key="editorKey"
          :draft="draft"
          :dirty="dirty"
          @save="saveWorkflow"
          @delete="confirmDelete"
          @cancel="cancelEdit"
        />
        <v-card v-else class="pa-8 text-center">
          <v-icon size="48" color="grey lighten-1">mdi-file-tree-outline</v-icon>
          <div class="mt-3 grey--text">
            Wähle links eine Vorlage zum Bearbeiten oder lege eine neue an.
          </div>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Vorlage löschen</v-card-title>
        <v-card-text>
          Vorlage „{{ pendingDeleteName }}" wird gelöscht. Bestehende Tickets, die mit dieser Vorlage angelegt wurden, sind nicht betroffen (Snapshot).
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Abbrechen</v-btn>
          <v-btn color="error" text @click="performDelete">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import WorkflowEditor from "./WorkflowEditor"

const cloneDraft = (wf) => JSON.parse(JSON.stringify(wf))

const emptyDraft = () => ({
  id: null,
  name: "",
  description: "",
  nodes: [],
})

export default {
  name: "WorkflowTab",
  components: { WorkflowEditor },
  data: () => ({
    search: "",
    selected_id: null,
    draft: null,
    saved_snapshot: null,
    editorKey: 0,
    deleteDialog: false,
    pendingDeleteId: null,
  }),
  computed: {
    workflows(){
      return this.$store.state.ticketsystem.workflows
    },
    filteredWorkflows(){
      const list = [...this.workflows].sort((a, b) => a.name.localeCompare(b.name))
      const q = (this.search || "").trim().toLowerCase()
      if (!q) return list
      return list.filter(w =>
        (w.name || "").toLowerCase().includes(q) ||
        (w.description || "").toLowerCase().includes(q)
      )
    },
    dirty(){
      if (!this.draft) return false
      if (!this.saved_snapshot) return true
      return JSON.stringify(this.draft) !== this.saved_snapshot
    },
    pendingDeleteName(){
      if (this.pendingDeleteId == null) return ""
      const wf = this.workflows.find(w => w.id === this.pendingDeleteId)
      return wf ? wf.name : ""
    },
  },
  watch: {
    workflows: {
      handler(){
        if (this.selected_id != null && this.draft && this.draft.id === this.selected_id) {
          const fresh = this.workflows.find(w => w.id === this.selected_id)
          if (fresh && !this.dirty) {
            this.loadDraft(fresh)
          }
        }
      },
      deep: true,
    },
  },
  mounted(){
    if (this.workflows.length === 0) {
      this.$store.dispatch("update", { context: "/workflows" })
    }
    if (this.$store.state.ticketsystem.users.length === 0) {
      this.$store.dispatch("update", { context: "/users" })
    }
  },
  methods: {
    isSelected(id){
      return this.selected_id === id
    },
    loadDraft(wf){
      this.draft = cloneDraft(wf)
      this.saved_snapshot = JSON.stringify(this.draft)
      this.editorKey++
    },
    selectWorkflow(id){
      if (this.dirty && !this.confirmDiscard()) return
      this.selected_id = id
      const wf = this.workflows.find(w => w.id === id)
      if (wf) this.loadDraft(wf)
    },
    newWorkflow(){
      if (this.dirty && !this.confirmDiscard()) return
      this.selected_id = null
      this.draft = emptyDraft()
      this.saved_snapshot = null
      this.editorKey++
    },
    cancelEdit(){
      if (this.dirty && !this.confirmDiscard()) return
      this.draft = null
      this.saved_snapshot = null
      this.selected_id = null
    },
    confirmDiscard(){
      return window.confirm("Du hast ungespeicherte Änderungen. Verwerfen?")
    },
    async saveWorkflow(draft){
      const body = {
        name: draft.name,
        description: draft.description || "",
        nodes: draft.nodes.map(n => ({
          name: n.name,
          assignee_type: n.assignee_type,
          assignee_user_id: n.assignee_type === "user" ? n.assignee_user_id : null,
          assignee_group: n.assignee_type === "group" ? n.assignee_group : null,
          checklist: (n.checklist || []).map(item => ({
            label: item.label,
            icon: item.icon || null,
          })),
        })),
      }
      if (draft.id) {
        await this.$store.dispatch("update", {
          payload_type: "PATCH",
          context: "/workflows/" + draft.id,
          payload: body,
        })
        const fresh = this.workflows.find(w => w.id === draft.id)
        if (fresh) this.loadDraft(fresh)
      } else {
        const data = await this.$store.dispatch("update", {
          payload_type: "POST",
          context: "/workflows",
          payload: body,
        })
        const newId = data && data.workflow && data.workflow.id
        const created = newId != null ? this.workflows.find(w => w.id === newId) : null
        if (created) {
          this.selected_id = created.id
          this.loadDraft(created)
        }
      }
    },
    confirmDelete(id){
      this.pendingDeleteId = id
      this.deleteDialog = true
    },
    async performDelete(){
      const id = this.pendingDeleteId
      this.deleteDialog = false
      this.pendingDeleteId = null
      if (id == null) return
      await this.$store.dispatch("update", {
        payload_type: "DELETE",
        context: "/workflows/" + id,
        skip_process: true,
      })
      this.$store.commit("remove_workflow", id)
      this.draft = null
      this.saved_snapshot = null
      this.selected_id = null
    },
  },
}
</script>

<style scoped>
.wf-list {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
